import { useCallback, useEffect, useMemo, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import MainContainer from 'components/layout/MainContainer/MainContainer'
import { useTimezone } from '../../../lib/TimezoneProvider'
import { useOrbit } from 'providers/OrbitProvider'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { someVendorPermitLandingPages } from 'lib/auth-helpers'
import {
  getAllAccessibleClientIds,
  getAllAccessibleVendorIds,
} from 'components/PerformanceTable/PerformanceTable'
import { failOnHttpError } from '../../../lib/utils'
import { LeadsDataGrid } from './table/LeadTableDataGrid'
import {
  buildInitialColumnVisibilityModel,
  buildInitialExternalFilters,
  buildInitialFilterModel,
  buildInitialPaginationModel,
  buildInitialSortModel,
  buildLeadsRequestBody,
  dashedToUnderscored,
  toggleStatusInExternalFilters,
  type ExternalFilters,
} from './leadTableQuery'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { getIn } from 'components/Formik/forms'
import UploadDialog from 'components/UploadDialog'
import Box from '@mui/material/Box'
import {
  GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import ExportStatusDialog, { ExportStatus } from './components/ExportStatusDialog'
import type { LeadMeta } from './table/leadTableTypes'
import { LeadCountRow } from './components/LeadCountRow'
import { useLeadTableMeta } from './useLeadTableMeta'
import { LeadsPageToolbar } from './LeadTableToolbar'
import { loadColumnOrder, loadColumnVisibilityModel, saveColumnOrder, saveColumnVisibilityModel } from './leadTablePreferences'


export default function LeadsPage() {
  const {
    currentOrganization,
    accessibleClients,
    accessibleVendorContracts,
    accessibleVendors,
    accessibleClientContracts,
    getClientRole,
  } = useCurrentUser()
  const { remote, getRemoteId } = useOrbit()
  const { timezone } = useTimezone()

  const [refreshKey, setRefreshKey] = useState(0)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [leadMeta, setLeadMeta] = useState<LeadMeta | undefined>()
  const [loading, setLoading] = useState(false)
  const [exportStatus, setExportStatus] = useState<ExportStatus|null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const permitAiAnalysis = !!getIn(currentOrganization, 'attributes.permitAiAnalysis')

  const allAccessibleClientIds = useMemo(() => {
    return getAllAccessibleClientIds(getRemoteId, accessibleClients, accessibleVendorContracts)
  }, [accessibleClients, accessibleVendorContracts, getRemoteId])

  const allAccessibleVendorIds = useMemo(() => {
    return getAllAccessibleVendorIds(getRemoteId, accessibleVendors, accessibleClientContracts)
  }, [accessibleVendors, accessibleClientContracts, getRemoteId])

  const userHasMultipleClients = allAccessibleClientIds.length > 1
  const userHasMultipleVendors = allAccessibleVendorIds.length > 1
  const permitLandingPages = someVendorPermitLandingPages({ accessibleVendors })
  const hasPermitEditRoleForSomeClient = useMemo(
    () => allAccessibleClientIds.some((clientId) => !!getIn(getClientRole(clientId), 'attributes.permitEdit')),
    [allAccessibleClientIds, getClientRole]
  )
  const permitUpload = hasPermitEditRoleForSomeClient
  const permitDelete = hasPermitEditRoleForSomeClient

  const {
    loadingMetadata,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    degreeProgramsByClient,
    statuses,
    dispositions,
    clientCampaignCustomAttributes,
  } = useLeadTableMeta({
    accessibleClientIds: allAccessibleClientIds,
  })

  const [externalFilters, setExternalFilters] = useState<ExternalFilters>(() =>
    buildInitialExternalFilters(timezone)
  )
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() =>
    buildInitialPaginationModel()
  )
  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    buildInitialSortModel()
  )
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() =>
    buildInitialFilterModel()
  )

  const [columnOrder, setColumnOrder] = useState<string[]>(() => loadColumnOrder())
  const handleColumnOrderChange = useCallback((nextColumnOrder: string[]) => {
    setColumnOrder(nextColumnOrder)
    saveColumnOrder(nextColumnOrder)
  }, [])

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => ({
    ...buildInitialColumnVisibilityModel([]),
    ...loadColumnVisibilityModel(),
  }))
  // clientCampaignCustomAttributes isn't available on initial render, so we rebuild the visibility
  // model once it is to hide custom columns
  useEffect(() => {
    setColumnVisibilityModel({
      ...buildInitialColumnVisibilityModel(clientCampaignCustomAttributes ?? []),
      ...loadColumnVisibilityModel(),
    })
  }, [clientCampaignCustomAttributes])

  const handleColumnVisibilityModelChange = useCallback((model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model)
    saveColumnVisibilityModel(model)
  }, [])

  const handleImport = useCallback(() => {
    setShowUploadDialog(true)
  }, [])

  const refreshTable = useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  const handleToggleStatusFilter = useCallback((status: string) => {
    const normalizedStatus = dashedToUnderscored(status)

    setExternalFilters((prev) => toggleStatusInExternalFilters(prev, normalizedStatus))
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setPaginationModel((prev) => ({
      ...buildInitialPaginationModel(),
      pageSize: prev.pageSize,
    }))
    setSortModel(buildInitialSortModel())
    setFilterModel(buildInitialFilterModel())
    setExternalFilters(buildInitialExternalFilters(timezone))
  }, [timezone])

  const handleExport = useCallback(async () => {
    if(exportStatus) {
      setShowExportModal(true)
      return
    }

    const requestBody = buildLeadsRequestBody({
      paginationModel,
      sortModel,
      filterModel,
      externalFilters,
      timezone,
    })

    setExportStatus({ status: 'running' })
    setShowExportModal(true)

    const urlBuilder = remote.requestProcessor.urlBuilder
    const baseURL = urlBuilder.resourceURL('lead')
    fetch(`${baseURL}.csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then(failOnHttpError)
      .then(response => response.json())
      .then(result => {
        setExportStatus({ status: 'queued', statusURL: result.status_url })
      })
      .catch(_error => {
        setExportStatus({ status: 'discarded', error: 'Failed to schedule export job' })
      })
  }, [exportStatus, paginationModel, sortModel, filterModel, externalFilters, timezone, remote.requestProcessor.urlBuilder])

  const gridProps = useMemo(() => {
    return {
      timezone,
      refreshKey,
      refreshTable,
      setLeadMeta,
      onLoadingChange: setLoading,
      paginationModel,
      onPaginationModelChange: setPaginationModel,
      sortModel,
      onSortModelChange: setSortModel,
      filterModel,
      columnVisibilityModel,
      onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
      columnOrder,
      onColumnOrderChange: handleColumnOrderChange,
      onFilterModelChange: setFilterModel,
      externalFilters,
      onExternalFiltersChange: setExternalFilters,
      clientIdToName,
      clientCampaignIdToName,
      vendorIdToName,
      degreeProgramIdToName,
      degreeProgramsByClient,
      statuses,
      dispositions,
      clientCampaignCustomAttributes,
      permitLandingPages,
      permitDelete,
      permitAiAnalysis,
      userHasMultipleClients,
      userHasMultipleVendors,
    }
  }, [
    timezone,
    refreshKey,
    refreshTable,
    paginationModel,
    sortModel,
    filterModel,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    columnOrder,
    handleColumnOrderChange,
    externalFilters,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    degreeProgramsByClient,
    statuses,
    dispositions,
    clientCampaignCustomAttributes,
    permitLandingPages,
    permitDelete,
    permitAiAnalysis,
    userHasMultipleClients,
    userHasMultipleVendors,
  ])

  if (loadingMetadata) {
    return (
      <MainContainer fullWidth>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </div>
      </MainContainer>
    )
  }

  return (
    <MainContainer
      fullWidth
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Paper
        sx={{
          my: '24px',
          p: '18px 24px',
          boxShadow: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 2, flexShrink: 0 }}
        >
          <Typography variant='h1'>Leads</Typography>

          <LeadsPageToolbar
            onResetFilters={handleResetFilters}
            onExport={handleExport}
            onImport={handleImport}
            onRefresh={refreshTable}
            exportStatus={exportStatus}
            permitUpload={permitUpload}
            loading={loading}
          />
        </Stack>

        <LeadCountRow
          leadMeta={leadMeta}
          externalFilters={externalFilters}
          onToggleStatusFilter={handleToggleStatusFilter}
          loading={loading}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <LeadsDataGrid {...gridProps} />
        </Box>
      </Paper>

      {!!showExportModal && (
        <ExportStatusDialog
          exportStatus={exportStatus}
          setExportStatus={setExportStatus}
          setShowExportModal={setShowExportModal}
        />
      )}
      {showUploadDialog && (
        <UploadDialog
          onComplete={refreshTable}
          onClose={() => setShowUploadDialog(false)}
        />
      )}
    </MainContainer>
  )
}
